// Section art from the Blueprint family, confined to the outer gutter beside a max-w-5xl
// column so it never sits under copy. Hidden below xl where there is no gutter. Each
// variant rides its own scroll rate. Parent section must be `relative isolate overflow-hidden`.
import { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';

type Props = {
  side?: 'left' | 'right';
  variant?: 'boxes' | 'mesh';
  strength?: number; // multiplies stroke opacity; 1 is the default weight
  className?: string;
};

const INK = 'var(--ink, #0a0a0b)';
const BLUE = '#2563eb';

export function Shard({ side = 'right', variant = 'boxes', strength = 1, className = '' }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [reduce ? 0 : 60, reduce ? 0 : -60]);
  const y2 = useTransform(scrollYProgress, [0, 1], [reduce ? 0 : 110, reduce ? 0 : -110]);
  const o = (v: number) => Math.min(1, v * strength);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-y-0 ${side === 'left' ? 'left-0' : 'right-0'} hidden xl:block w-[calc(50vw-560px)] -z-10 ${className}`}
      style={side === 'left' ? { transform: 'scaleX(-1)' } : undefined}
    >
      {variant === 'boxes' && (
        <>
          <motion.svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 200 800"
            preserveAspectRatio="xMidYMid meet"
            style={{ y }}
          >
            {[
              [20, 80, 160, 120],
              [20, 200, 70, 90],
              [90, 200, 90, 160],
              [20, 290, 70, 70],
              [20, 360, 160, 110],
              [110, 470, 70, 120],
              [20, 470, 90, 60],
            ].map(([x, yy, w, h], i) => (
              <rect
                key={i}
                x={x}
                y={yy}
                width={w}
                height={h}
                fill="none"
                stroke={INK}
                strokeWidth="1.25"
                opacity={o(0.22)}
              />
            ))}
          </motion.svg>
          <motion.svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 200 800"
            preserveAspectRatio="xMidYMid meet"
            style={{ y: y2 }}
          >
            <rect x="20" y="640" width="22" height="22" fill={BLUE} />
            <rect x="52" y="640" width="22" height="22" fill={BLUE} />
          </motion.svg>
        </>
      )}

      {variant === 'mesh' && (
        <>
          <motion.svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 200 800"
            preserveAspectRatio="xMidYMid meet"
            style={{ y }}
          >
            {(
              [
                [
                  [40, 140],
                  [120, 200],
                ],
                [
                  [120, 200],
                  [60, 300],
                ],
                [
                  [60, 300],
                  [150, 360],
                ],
                [
                  [150, 360],
                  [90, 460],
                ],
                [
                  [120, 200],
                  [150, 360],
                ],
                [
                  [40, 140],
                  [60, 300],
                ],
                [
                  [90, 460],
                  [40, 540],
                ],
              ] as number[][][]
            ).map(([a, b], i) => (
              <path
                key={i}
                d={`M ${a[0]} ${a[1]} L ${b[0]} ${b[1]}`}
                stroke={INK}
                strokeWidth="1"
                opacity={o(0.4)}
              />
            ))}
          </motion.svg>
          <motion.svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 200 800"
            preserveAspectRatio="xMidYMid meet"
            style={{ y: y2 }}
          >
            {(
              [
                [40, 140],
                [120, 200],
                [60, 300],
                [150, 360],
                [90, 460],
                [40, 540],
              ] as number[][]
            ).map(([x, yy], i) =>
              i === 2 || i === 4 ? (
                <circle key={i} cx={x} cy={yy} r="5" fill={BLUE} />
              ) : (
                <circle
                  key={i}
                  cx={x}
                  cy={yy}
                  r="4"
                  fill="var(--paper, #fafafa)"
                  stroke={INK}
                  strokeWidth="1.5"
                  opacity={o(0.9)}
                />
              )
            )}
          </motion.svg>
        </>
      )}
    </div>
  );
}

export default Shard;
